/**
 * useChapterStackSync - Sync between BookContext chapters and BufferSyncContext stack
 *
 * On chapter activation: hydrates chapter → loads card buffers → replaces stack.
 * Also restores outline state and draft from chapter hydration.
 *
 * @module @humanizer/studio/hooks/useChapterStackSync
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useBook } from '../contexts/BookContext';
import { useBufferSync, type StackItem } from '../contexts/BufferSyncContext';
import { cleanBufferName } from '../contexts/BufferSyncContext';
import type { OutlineStateDataElectron } from '../types/electron';

export interface ChapterDraftRestoration {
  draftText: string;
  draftBufferId: string;
}

export function useChapterStackSync() {
  const {
    activeChapterId,
    hydrateChapter,
  } = useBook();

  const {
    setStackFromHydration,
    stackItems,
  } = useBufferSync();

  const prevChapterId = useRef<string | null>(null);

  // Restored state from chapter hydration
  const [restoredOutline, setRestoredOutline] = useState<OutlineStateDataElectron | null>(null);
  const [restoredDraft, setRestoredDraft] = useState<ChapterDraftRestoration | null>(null);

  // Allow MainWorkspace to consume and clear restored state
  const consumeRestoredOutline = useCallback(() => {
    const outline = restoredOutline;
    setRestoredOutline(null);
    return outline;
  }, [restoredOutline]);

  const consumeRestoredDraft = useCallback(() => {
    const draft = restoredDraft;
    setRestoredDraft(null);
    return draft;
  }, [restoredDraft]);

  useEffect(() => {
    // Skip if chapter didn't change
    if (activeChapterId === prevChapterId.current) return;
    prevChapterId.current = activeChapterId;

    if (!activeChapterId) return;

    // Clear restored state from previous chapter before async hydration
    setRestoredOutline(null);
    setRestoredDraft(null);

    // Hydrate the chapter and load its cards into the stack
    (async () => {
      const hydration = await hydrateChapter(activeChapterId);
      if (!hydration) return;

      const { cardBufferIds, outlineState, draftBufferId } = hydration;

      // Restore outline state if present
      if (outlineState) {
        setRestoredOutline(outlineState);
      }

      // Restore draft if a draft buffer is linked
      if (draftBufferId && window.electronBuffer?.load) {
        try {
          const draftBuf = await window.electronBuffer.load(draftBufferId);
          if (draftBuf && draftBuf.text) {
            setRestoredDraft({ draftText: draftBuf.text, draftBufferId });
          }
        } catch {
          // Draft buffer may have been deleted
        }
      }

      // Load all buffers in parallel to create StackItems
      const loadResults = await Promise.allSettled(
        cardBufferIds.map((bufferId, i) =>
          window.electronBuffer?.load(bufferId).then(buf => ({ buf, bufferId, i }))
        )
      );

      const items: StackItem[] = [];
      for (const result of loadResults) {
        if (result.status !== 'fulfilled' || !result.value?.buf) continue;
        const { buf, bufferId, i } = result.value;

        const text = buf.text ?? '';
        const userTitle = buf.origin?.metadata?.title as string | undefined;
        const firstLine = text.replace(/<!--[^>]*-->\n?/g, '').trim().split('\n')[0]?.trim() ?? '';
        const summary = userTitle ?? cleanBufferName(firstLine, bufferId.slice(0, 8));

        items.push({
          bufferId,
          contentItem: {
            id: buf.id,
            type: 'text',
            text,
            metadata: {
              wordCount: buf.wordCount,
              source: buf.origin ? {
                type: buf.origin.sourceType ?? 'buffer',
                path: [],
                nodeId: buf.origin.sourceNodeId,
              } : undefined,
            },
          },
          summary,
          source: buf.origin?.sourceType ?? 'buffer',
          wordCount: buf.wordCount ?? 0,
          sourceNodeId: buf.origin?.sourceNodeId,
          sortOrder: i,
        });
      }

      // Maintain original sort order after parallel loading
      items.sort((a, b) => a.sortOrder - b.sortOrder);

      // Always replace stack — even empty items clears stale cards from previous chapter
      setStackFromHydration(items);
    })();
  }, [activeChapterId, hydrateChapter, setStackFromHydration]);

  return { stackItems, restoredOutline, restoredDraft, consumeRestoredOutline, consumeRestoredDraft };
}
